import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Quote } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { submitToCustomerIO } from '@/utils/apiUtils';

const IBS2026 = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [timing, setTiming] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitToCustomerIO({
        email,
        name,
        company,
        message: `IBS 2026 | Timing: ${timing || 'Not specified'} | Message: ${message}`
      }, 'ibs-2026');
      toast({
        title: "Request submitted!",
        description: "We'll be in touch shortly."
      });
      setEmail('');
      setName('');
      setCompany('');
      setTiming('');
      setMessage('');
    } catch (error) {
      toast({
        title: "Error submitting request",
        description: error instanceof Error ? error.message : "Please try again later.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Fordje at IBS 2026",
    "description": "Meet Fordje at NAHB's International Builders' Show 2026",
    "url": "https://www.fordje.com/ibs-2026"
  };

  return (
    <div className="min-h-screen bg-fordje-navy-dark relative overflow-hidden">
      <Helmet>
        <title>Fordje at IBS 2026 | NAHB International Builders' Show</title>
        <meta name="robots" content="noindex, follow" />
        <meta name="description" content="Meet Fordje at NAHB's International Builders' Show 2026. See how builders use Fordje to know exactly what's required in any jurisdiction." />
        <link rel="canonical" href="https://www.fordje.com/ibs-2026" />
        <meta property="og:title" content="Fordje at IBS 2026" />
        <meta property="og:description" content="Stop losing projects to city code surprises." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.fordje.com/ibs-2026" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'linear-gradient(hsl(var(--primary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)',
        backgroundSize: '60px 60px'
      }} />

      <main className="relative z-10 flex flex-col px-5 sm:px-6 pt-8 pb-10 max-w-lg mx-auto w-full min-h-screen">
        {/* Logo */}
        <div className="mb-8 flex items-center gap-2">
          <img
            src="/lovable-uploads/595a50f5-c631-4403-8464-cad23f811cfe.png"
            alt="Fordje Logo"
            className="h-8 w-8"
          />
          <span className="text-xl font-bold font-ubuntu text-white">Fordje</span>
        </div>

        {/* Hero */}
        <h1 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight mb-4">
          Stop Losing Projects to City Code{' '}
          <span className="gradient-text">Surprises</span>
        </h1>
        <p className="text-lg sm:text-xl text-fordje-gray leading-relaxed mb-6">
          See how builders use Fordje to know exactly what's required in any jurisdiction — before they design or submit permits.
        </p>

        {/* Social Proof */}
        <div className="mb-8">
          <Quote className="h-5 w-5 text-fordje-accent/40 mb-2" />
          <p className="text-base sm:text-lg text-white/80 italic leading-relaxed">
            "What used to take a full week of research now takes an hour."
          </p>
          <p className="text-sm text-fordje-accent font-semibold mt-1.5">— Permitting Lead</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name" className="text-fordje-gray text-base">Full Name</Label>
            <Input
              id="name"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              placeholder="Your name"
              className="mt-1.5 h-12 text-base bg-fordje-navy-light/50 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-fordje-accent"
              maxLength={100}
              autoComplete="name"
            />
          </div>

          <div>
            <Label htmlFor="email" className="text-fordje-gray text-base">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              placeholder="you@company.com"
              className="mt-1.5 h-12 text-base bg-fordje-navy-light/50 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-fordje-accent"
              maxLength={100}
              autoComplete="email"
              inputMode="email"
            />
          </div>

          <div>
            <Label htmlFor="company" className="text-fordje-gray text-base">Company</Label>
            <Input
              id="company"
              value={company}
              onChange={e => setCompany(e.target.value)}
              required
              placeholder="Your company"
              className="mt-1.5 h-12 text-base bg-fordje-navy-light/50 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-fordje-accent"
              maxLength={100}
              autoComplete="organization"
            />
          </div>

          <div>
            <Label htmlFor="timing" className="text-fordje-gray text-base">When works best?</Label>
            <select
              id="timing"
              value={timing}
              onChange={e => setTiming(e.target.value)}
              className="mt-1.5 flex h-12 w-full rounded-md border border-white/10 bg-fordje-navy-light/50 px-3 py-2 text-base text-white ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fordje-accent focus-visible:ring-offset-2 appearance-none"
            >
              <option value="" className="bg-fordje-navy">Select an option</option>
              <option value="meet-at-ibs" className="bg-fordje-navy">Meet at IBS this week</option>
              <option value="book-demo-next-week" className="bg-fordje-navy">Book a demo for next week</option>
            </select>
          </div>

          <div>
            <Label htmlFor="message" className="text-fordje-gray text-base">
              Message <span className="text-white/30 font-normal">(optional)</span>
            </Label>
            <Textarea
              id="message"
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Anything we should know?"
              className="mt-1.5 min-h-[80px] text-base bg-fordje-navy-light/50 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-fordje-accent"
              maxLength={500}
            />
          </div>

          <Button
            type="submit"
            className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-fordje-blue to-fordje-accent hover:from-fordje-blue-dark hover:to-fordje-blue shadow-lg shadow-fordje-blue/20 text-white mt-2"
            disabled={loading}
          >
            {loading ? 'Submitting...' : "Let's Talk"}
          </Button>
        </form>

        {/* Footer */}
        <div className="mt-10 text-center">
          <p className="text-sm font-semibold text-white">
            Make every city <span className="gradient-text">predictable.</span>
          </p>
          <p className="text-xs text-white/40 mt-2">
            We respect your privacy and will never share your information.
          </p>
        </div>
      </main>
    </div>
  );
};

export default IBS2026;
